<template>
  <div class="hourly-cost">
    <BaseDetails>
      <template v-slot:header>
        <h2 class="pb-3">Pay Rate</h2>
        <Permissioned section="bookings.edit">
          <EditPayRates
            permission="worker-cost"
            :is-open="isEditOpen"
            @close="closeHourlyRateEdit"
            @edit="editPayRates"
          />
          <PrimaryButton
            v-if="!isFetchingGroupedCustomAgencyFee"
            permission="worker-cost"
            @click.native="openHourlyRateEdit"
            light
            dense
            shrink
          >
            Edit
            <template v-slot:append:icon>
              <v-icon class="ml-1">mdi-pencil</v-icon>
            </template>
          </PrimaryButton>
        </Permissioned>
      </template>
      <template v-slot:body>
        <v-skeleton-loader
          v-if="isFetchingGroupedCustomAgencyFee"
          type="table"
        />
        <template v-else>
          <div
            v-for="agency in groupedCustomAgencyFees"
            :key="agency.agency_name"
          >
            <div class="agency--cost">
              <strong>{{ agency.agency_name }}</strong>
              <span>£{{ agency.total_cost }}</span>
            </div>
            <div
              v-for="applicant in agency.applicants"
              :key="applicant.fullname"
              class="applicant"
            >
              <span class="indent">{{ applicant.fullname }}</span>
              <span>£{{ applicant.total_cost }}</span>
            </div>
          </div>
        </template>
      </template>
    </BaseDetails>
  </div>
</template>

<script>
import sortBy from "lodash/sortBy";
import isEmpty from "lodash/isEmpty";
import BaseDetails from "@/components/common/BaseDetails/BaseDetails";
import PrimaryButton from "@/components/common/Button/PrimaryButton";
import EditPayRates from "@/views/bookings/EditBooking/EditPayRates";
import Permissioned from "@/components/common/Permissioned";
import { createNamespacedHelpers } from "vuex";
import { CUSTOM_AGENCY_FEE_NAMESPACE } from "@/store/modules/custom-agency-pay-rates";
import {
  BATCH_UPDATE_RATES,
  FETCH_GROUPED_AGENCY_FEES
} from "@/store/modules/custom-agency-pay-rates/actions";
import {
  BOOKINGS_NAMESPACE,
  EDIT_TIME_UNIT_AND_PAYRATES
} from "@/store/modules/bookings/actions";

const { mapState, mapActions } = createNamespacedHelpers(BOOKINGS_NAMESPACE);
const {
  mapActions: mapCustomAgencyFeeActions,
  mapState: mapCustomAgencyFeeState
} = createNamespacedHelpers(CUSTOM_AGENCY_FEE_NAMESPACE);

export default {
  name: "HourlyCost",
  components: { EditPayRates, PrimaryButton, BaseDetails, Permissioned },
  async created() {
    await this.fetchCustomAgencyPayRates({
      bookingId: this.currentBookingId
    });
  },
  data: () => ({
    isEditOpen: false,
    selectedTab: null
  }),
  methods: {
    openHourlyRateEdit() {
      this.isEditOpen = true;
    },
    closeHourlyRateEdit() {
      this.isEditOpen = false;
    },
    async editPayRates({ bookingId, customAgencyRates, payRates }) {
      if (payRates && payRates.length > 0) {
        await this.editTimeUnit({
          bookingId,
          data: { payRates, timeUnitName: this.currentTimeUnit },
          hideMessage: true
        });
      }
      const isSuccess = await this.editCustomAgencyFees({
        bookingId,
        customAgencyRates
      });
      if (isSuccess) {
        this.isEditOpen = false;
      }
    },
    isTotalWorkerCost(name) {
      return name === "Total Worker Cost";
    },
    ...mapCustomAgencyFeeActions({
      fetchCustomAgencyPayRates: FETCH_GROUPED_AGENCY_FEES,
      editCustomAgencyFees: BATCH_UPDATE_RATES
    }),
    ...mapActions({
      editTimeUnit: EDIT_TIME_UNIT_AND_PAYRATES
    }),
    sortBy,
    isEmpty
  },
  computed: {
    ...mapCustomAgencyFeeState({
      groupedCustomAgencyFees: state => state.groupedCustomAgencyFees,
      isFetchingGroupedCustomAgencyFee: state =>
        state.isFetchingGroupedCustomAgencyFee
    }),
    ...mapState({
      currentBookingId: state => state.currentBooking.id,
      currentBookingTitle: state => state.currentBooking.title,
      currentClientId: state => state.currentBooking.client.id,
      currentTimeUnit: state => state.currentBooking.timeUnitName
    })
  }
};
</script>

<style lang="scss">
.agency--cost {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
}

.applicant {
  display: flex;
  justify-content: space-between;
  padding-left: 1.5rem;
  padding: 0.5rem 0;
}

.indent {
  padding-left: 2rem;
}
</style>
